import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Plus, User, Phone, Mail, Tag, MoreVertical, Filter, Calendar, Users, Trash2, Building2, ShieldCheck, ChevronDown, Check, FileText, MapPin, Clock, DollarSign, ExternalLink, UserPlus, Sparkles } from "lucide-react";
import { Link } from "wouter";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ListPagination } from "@/components/ui/list-pagination";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useListPatients, getListPatientsQueryKey, useRemovePatientFromCareTeam, useGetProviderAppointments, useListProviderEstimates, useSearchPatient, useRequestCareTeamLink, useInvitePatientEmail, API_BASE } from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Patients() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedProviderFilter, setSelectedProviderFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [addModalTab, setAddModalTab] = useState<"search" | "invite">("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Patient Profile & History Modal State
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [profileTab, setProfileTab] = useState<"overview" | "visits" | "estimates">("overview");

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const removePatient = useRemovePatientFromCareTeam();
  const requestCareTeam = useRequestCareTeamLink();
  const invitePatient = useInvitePatientEmail();

  const { data: searchResults = [], isLoading: isSearching } = useSearchPatient(searchQuery);

  const { data: appointmentsRaw } = useGetProviderAppointments({ limit: 1000 });
  const { data: estimatesRaw } = useListProviderEstimates();

  const handleOpenPatientProfile = (patient: any) => {
    setSelectedPatient(patient);
    setProfileTab("overview");
    setProfileModalOpen(true);
  };

  const patientAppointments = useMemo(() => {
    if (!selectedPatient || !appointmentsRaw) return [];
    const apptsList: any[] = Array.isArray(appointmentsRaw)
      ? appointmentsRaw
      : (appointmentsRaw?.items || appointmentsRaw?.appointments || []);

    const targetUserId = String(selectedPatient.userId || selectedPatient.id || "");
    const targetEmail = (selectedPatient.email || "").toLowerCase();
    const targetName = (selectedPatient.name || "").toLowerCase();

    return apptsList.filter((a: any) => {
      const matchUserId = String(a.memberId || "") === targetUserId || String(a.member?.userId || "") === targetUserId || String(a.member?.id || "") === targetUserId;
      const matchEmail = targetEmail && (a.member?.user?.email?.toLowerCase() === targetEmail || a.member?.email?.toLowerCase() === targetEmail);
      const matchName = targetName && (a.patientName?.toLowerCase() === targetName || (a.member?.user?.firstName && targetName.includes(a.member.user.firstName.toLowerCase())));
      return matchUserId || matchEmail || matchName;
    });
  }, [selectedPatient, appointmentsRaw]);

  const patientEstimates = useMemo(() => {
    if (!selectedPatient || !estimatesRaw) return [];
    const estList: any[] = Array.isArray(estimatesRaw)
      ? estimatesRaw
      : (estimatesRaw?.items || estimatesRaw?.estimates || []);

    const targetUserId = String(selectedPatient.userId || selectedPatient.id || "");
    const targetEmail = (selectedPatient.email || "").toLowerCase();

    return estList.filter((e: any) => {
      const matchUserId = String(e.memberId || "") === targetUserId || String(e.member?.userId || "") === targetUserId || String(e.member?.id || "") === targetUserId;
      const matchEmail = targetEmail && e.member?.email?.toLowerCase() === targetEmail;
      return matchUserId || matchEmail;
    });
  }, [selectedPatient, estimatesRaw]);

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch (e) { }
    }
  }, []);
  const isClinic = user?.provider?.practiceType === "group" || localStorage.getItem("practiceType") === "group";

  const params: Record<string, string> = {};
  if (search) params.q = search;
  if (status && status !== "all") params.status = status;
  params.page = page.toString();
  params.limit = limit.toString();

  const { data: patientsData, isLoading } = useListPatients(params);
  const rawPatients = patientsData?.items || [];
  const totalPages = patientsData?.totalPages || 1;
  const totalCount = patientsData?.totalCount || 0;

  // Compute Locations List for filtering
  const locationOptions = useMemo(() => {
    const setLoc = new Set<string>();
    rawPatients.forEach((p: any) => {
      const locStr = [p.city, p.state].filter(Boolean).join(", ");
      if (locStr) setLoc.add(locStr);
    });
    return Array.from(setLoc);
  }, [rawPatients]);

  // Compute Providers List for clinic practice filtering
  const providerOptions = useMemo(() => {
    const mapProv = new Map<string, string>();
    rawPatients.forEach((p: any) => {
      if (p.providerId && p.providerName) {
        mapProv.set(p.providerId, p.providerName);
      }
    });
    return Array.from(mapProv.entries()).map(([id, name]) => ({ id, name }));
  }, [rawPatients]);

  // Filtered Patients List based on Location and Provider
  const patients = useMemo(() => {
    return rawPatients.filter((p: any) => {
      const locStr = [p.city, p.state].filter(Boolean).join(", ");
      const matchLocation = selectedLocation === "all" || locStr === selectedLocation;
      const matchProvider = selectedProviderFilter === "all" || p.providerId === selectedProviderFilter;
      return matchLocation && matchProvider;
    });
  }, [rawPatients, selectedLocation, selectedProviderFilter]);

  return (
    <ProviderLayout>
      <div className="w-full p-3 sm:p-6 md:p-8 lg:p-10 xl:px-12 flex flex-col min-h-[calc(100vh-8rem)]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Patients in Practice</h1>
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
                {isLoading ? "..." : `${patients.length} patient${patients.length === 1 ? '' : 's'} displayed`}
              </p>
            </div>
          </div>

          <Dialog open={open} onOpenChange={(val) => {
            setOpen(val);
            if (!val) {
              setSearchQuery("");
              setInviteEmail("");
              setInviteName("");
            }
          }}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto h-10 sm:h-9 gap-2 rounded-xl" data-testid="button-add-patient">
                <UserPlus className="w-4 h-4" /> Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[94vw] max-w-[600px] max-h-[85vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-primary" />
                  Add Patient to Care Team
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Search for existing registered patients by Patient ID, email or phone, or send an email invitation to new patients.
                </DialogDescription>
              </DialogHeader>

              <Tabs value={addModalTab} onValueChange={(t) => setAddModalTab(t as any)} className="w-full mt-2">
                <TabsList className="grid grid-cols-2 w-full h-11 bg-muted/60 p-1 rounded-2xl">
                  <TabsTrigger value="search" className="rounded-xl text-xs font-semibold">1. Search Registered Patient</TabsTrigger>
                  <TabsTrigger value="invite" className="rounded-xl text-xs font-semibold">2. Invite New Patient</TabsTrigger>
                </TabsList>

                {/* Tab 1: Search Patient */}
                <TabsContent value="search" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Search Registered Patients</Label>
                    <div className="relative flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by Patient ID (e.g. DC-ED5A10F3), Email, Phone, or Name..."
                          className="pl-10 rounded-xl text-sm h-11"
                          autoFocus
                        />
                      </div>
                    </div>
                  </div>

                  {/* Search Results */}
                  <div className="space-y-3 pt-1">
                    {searchQuery.trim().length === 0 ? (
                      <div className="p-8 text-center border border-dashed border-border rounded-2xl bg-muted/20">
                        <Search className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground font-medium">Enter Patient ID, email, or name to search.</p>
                      </div>
                    ) : isSearching ? (
                      <div className="space-y-2">
                        <Skeleton className="h-16 w-full rounded-2xl" />
                        <Skeleton className="h-16 w-full rounded-2xl" />
                      </div>
                    ) : searchResults && searchResults.length > 0 ? (
                      <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
                        {searchResults.map((patient: any) => (
                          <div key={patient.id} className="p-3.5 border border-border rounded-2xl bg-card hover:bg-accent/40 transition-colors flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10 border border-primary/20">
                                <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                                  {patient.name?.split(" ").map((n: string) => n[0]).join("").slice(0, 2) || "P"}
                                </AvatarFallback>
                              </Avatar>
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-sm text-foreground">{patient.name}</p>
                                  <Badge variant="outline" className="text-[10px] font-mono border-primary/30 text-primary bg-primary/5 px-1.5 py-0">
                                    {patient.patientId}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground flex items-center gap-2">
                                  <span>{patient.email}</span>
                                  {patient.phone && patient.phone !== 'N/A' && <span>• {patient.phone}</span>}
                                </p>
                              </div>
                            </div>

                            <div>
                              {patient.careTeamStatus === 'ACTIVE' ? (
                                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-0 font-medium text-xs rounded-xl px-2.5 py-1">
                                  In Care Team
                                </Badge>
                              ) : patient.careTeamStatus === 'PENDING' ? (
                                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-0 font-medium text-xs rounded-xl px-2.5 py-1">
                                  Request Pending
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  disabled={requestCareTeam.isPending}
                                  onClick={() => {
                                    requestCareTeam.mutate({ patientUserId: patient.id }, {
                                      onSuccess: () => {
                                        toast({
                                          title: "Connection Request Sent",
                                          description: `Care team connection request sent to ${patient.name}. The patient will receive a notification to approve.`
                                        });
                                        queryClient.invalidateQueries({ queryKey: ['searchPatient'] });
                                        queryClient.invalidateQueries({ queryKey: getListPatientsQueryKey() });
                                      },
                                      onError: (err: any) => {
                                        toast({ title: "Failed to send request", description: err.message, variant: "destructive" });
                                      }
                                    });
                                  }}
                                  className="rounded-xl gap-1.5 text-xs font-semibold"
                                >
                                  <UserPlus className="w-3.5 h-3.5" />
                                  {requestCareTeam.isPending ? "Sending..." : "Send Request"}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 text-center border border-border rounded-2xl bg-muted/20 space-y-2">
                        <p className="text-sm font-semibold text-foreground">No registered patients found</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tab 2: Invite New Patient */}
                <TabsContent value="invite" className="space-y-4 pt-4">
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="inviteEmail" className="text-xs font-semibold">Patient Email Address *</Label>
                      <Input
                        id="inviteEmail"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="e.g. patient@example.com"
                        className="rounded-xl h-11"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="inviteName" className="text-xs font-semibold">Patient Full Name (Optional)</Label>
                      <Input
                        id="inviteName"
                        type="text"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        placeholder="e.g. Jane Doe"
                        className="rounded-xl h-11"
                      />
                    </div>

                    <Button
                      type="button"
                      disabled={invitePatient.isPending || !inviteEmail}
                      onClick={() => {
                        if (!inviteEmail || !inviteEmail.includes('@')) {
                          toast({ title: "Invalid Email", description: "Please enter a valid email address.", variant: "destructive" });
                          return;
                        }
                        invitePatient.mutate({ email: inviteEmail, name: inviteName }, {
                          onSuccess: () => {
                            toast({
                              title: "Invitation Sent",
                              description: `An invitation email has been sent to ${inviteEmail}.`
                            });
                            setOpen(false);
                            setInviteEmail("");
                            setInviteName("");
                          },
                          onError: (err: any) => {
                            toast({ title: "Failed to send invitation", description: err.message, variant: "destructive" });
                          }
                        });
                      }}
                      className="w-full rounded-xl h-11 gap-2 mt-2 font-semibold"
                    >
                      <Mail className="w-4 h-4" />
                      {invitePatient.isPending ? "Sending Invitation..." : "Send Email Invitation"}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters Row (Provider / Practitioner & Location Filters) */}
        <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 mb-4 sm:mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient name, email, phone..." className="pl-10 rounded-xl h-10 text-xs sm:text-sm" data-testid="input-search-patients" />
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Location Filter */}
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-full sm:w-44 rounded-xl h-10 text-xs sm:text-sm">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locationOptions.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Provider / Practitioner Filter */}
            {providerOptions.length > 0 && (
              <Select value={selectedProviderFilter} onValueChange={setSelectedProviderFilter}>
                <SelectTrigger className="w-full sm:w-44 rounded-xl h-10 text-xs sm:text-sm">
                  <SelectValue placeholder="All Practitioners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Practitioners</SelectItem>
                  {providerOptions.map(prov => (
                    <SelectItem key={prov.id} value={prov.id}>{prov.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className={`${providerOptions.length > 0 ? 'col-span-2 sm:col-span-1' : ''} w-full sm:w-36 rounded-xl h-10 text-xs sm:text-sm`} data-testid="select-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Patients Card View (only for mobile screens) */}
        <div className="space-y-3 block md:hidden">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-4 rounded-2xl border border-border bg-card space-y-3 shadow-2xs">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-9 w-full rounded-xl" />
              </div>
            ))
          ) : (patients?.length ?? 0) === 0 ? (
            <div className="p-8 text-center bg-card rounded-2xl border border-border text-muted-foreground text-sm">
              <User className="w-10 h-10 mx-auto mb-2 opacity-30" />
              No matching patients found
            </div>
          ) : (
            patients?.map((p, i) => {
              const memberIdStr = p.patientId || (p.userId ? `DC-${String(p.userId).slice(0, 8).toUpperCase()}` : `DC-${String(p.id).slice(0, 8).toUpperCase()}`);
              const targetMemberId = p.userId || p.id;
              const dobStr = p.dateOfBirth || p.dob || '—';

              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={() => handleOpenPatientProfile(p)}
                  className="bg-card rounded-2xl border border-border p-3.5 sm:p-4 shadow-2xs hover:border-primary/40 transition-all cursor-pointer space-y-3"
                  data-testid={`patient-card-${p.id}`}
                >
                  {/* Card Header: Avatar, Name, Member ID, Actions Dropdown */}
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="w-10 h-10 border border-primary/20 shrink-0">
                        {p.profilePicture && (
                          <AvatarImage src={`${API_BASE.replace(/\/v1|\/api/, '')}${p.profilePicture}`} alt={p.name} className="object-cover" />
                        )}
                        <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                          {p.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-bold text-sm text-foreground truncate">{p.name}</p>
                          <span className="font-mono text-[10px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-md shrink-0">
                            {memberIdStr}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{p.gender || 'Patient'}</p>
                      </div>
                    </div>

                    <div onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="w-8 h-8 rounded-xl shrink-0">
                            <MoreVertical className="w-4 h-4 text-slate-500 hover:text-slate-700" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="cursor-pointer gap-2 font-medium text-primary text-xs" onClick={() => handleOpenPatientProfile(p)}>
                            <User className="w-3.5 h-3.5" /> View Patient Profile
                          </DropdownMenuItem>
                          <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(targetMemberId)}&memberName=${encodeURIComponent(p.name)}`}>
                            <DropdownMenuItem className="cursor-pointer gap-2 font-bold text-emerald-600 dark:text-emerald-400 text-xs">
                              <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                            </DropdownMenuItem>
                          </Link>
                          <Link href={`/provider/appointments?search=${encodeURIComponent(p.name)}`}>
                            <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                              <Calendar className="w-3.5 h-3.5 text-slate-500" /> View Appointments
                            </DropdownMenuItem>
                          </Link>
                          {p.addedInCareTeam && p.userId && (
                            <DropdownMenuItem
                              className="cursor-pointer gap-2 text-red-600 focus:text-red-600 text-xs"
                              onClick={() => {
                                if (window.confirm("Remove patient from your care team?")) {
                                  removePatient.mutate({ patientId: p.userId }, {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: getListPatientsQueryKey() });
                                      toast({ title: "Removed from care team", description: "Patient has been removed." });
                                    }
                                  });
                                }
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Remove from care team
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Card Middle: Key Metadata (DOB, Phone, Email) */}
                  <div className="grid grid-cols-2 gap-2 text-[11px] pt-1.5 border-t border-border/50 text-muted-foreground">
                    <div>
                      <span className="text-[10px] uppercase font-semibold text-muted-foreground/70 block">DOB</span>
                      <span className="font-medium text-foreground">{dobStr}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-semibold text-muted-foreground/70 block">Phone</span>
                      <span className="font-medium text-foreground truncate block">{p.phone || '—'}</span>
                    </div>
                    {p.email && (
                      <div className="col-span-2">
                        <span className="text-[10px] uppercase font-semibold text-muted-foreground/70 block">Email</span>
                        <span className="font-medium text-foreground truncate block">{p.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Card Bottom: Care Team Status + GFE Action */}
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/50">
                    <div>
                      {p.addedInCareTeam ? (
                        <Badge className="text-[11px] border-0 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 font-semibold px-2 py-0.5 rounded-full">
                          {p.addedInCareTeamDate ? `In Team (${new Date(p.addedInCareTeamDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})` : "In Care Team"}
                        </Badge>
                      ) : (
                        <Badge className="text-[11px] border-0 bg-muted text-muted-foreground font-medium px-2 py-0.5 rounded-full">
                          No Care Team
                        </Badge>
                      )}
                    </div>

                    <div onClick={e => e.stopPropagation()}>
                      <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(targetMemberId)}&memberName=${encodeURIComponent(p.name)}`}>
                        <Button size="sm" className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1 text-xs rounded-xl shadow-xs">
                          <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Compact Patients Table (for tablet & desktop screens) */}
        <div className="hidden md:block bg-card rounded-2xl border border-border shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs min-w-[760px]">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-muted-foreground font-semibold uppercase tracking-wider">
                  <th className="py-3 px-4">Member ID</th>
                  <th className="py-3 px-4">Patient</th>
                  <th className="py-3 px-4 hidden md:table-cell">DOB</th>
                  <th className="py-3 px-4 hidden lg:table-cell">Phone</th>
                  <th className="py-3 px-4 hidden xl:table-cell">Email</th>
                  <th className="py-3 px-4">In Patient Care Team</th>
                  <th className="py-3 px-4 text-right">GFE Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isLoading ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={7} className="px-4 py-3"><Skeleton className="h-9 w-full" /></td></tr>
                )) : (patients?.length ?? 0) === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    <User className="w-10 h-10 mx-auto mb-2 opacity-30" />No matching patients found
                  </td></tr>
                ) : patients?.map((p, i) => {
                  const memberIdStr = p.patientId || (p.userId ? `DC-${String(p.userId).slice(0, 8).toUpperCase()}` : `DC-${String(p.id).slice(0, 8).toUpperCase()}`);
                  const targetMemberId = p.userId || p.id;
                  const dobStr = p.dateOfBirth || p.dob || '—';

                  return (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => handleOpenPatientProfile(p)}
                      className="hover:bg-accent/60 transition-colors cursor-pointer group"
                      data-testid={`patient-row-${p.id}`}
                    >
                      {/* Member ID */}
                      <td className="py-2.5 px-4">
                        <span className="font-mono text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-md group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {memberIdStr}
                        </span>
                      </td>

                      {/* Patient */}
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2.5">
                          <Avatar className="w-8 h-8 border border-primary/20 group-hover:scale-105 transition-transform flex-shrink-0">
                            {p.profilePicture && (
                              <AvatarImage src={`${API_BASE.replace(/\/v1|\/api/, '')}${p.profilePicture}`} alt={p.name} className="object-cover" />
                            )}
                            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                              {p.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-bold text-xs text-foreground group-hover:text-primary group-hover:underline transition-colors truncate">{p.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{p.gender || 'Patient'}</p>
                          </div>
                        </div>
                      </td>

                      {/* DOB */}
                      <td className="py-2.5 px-4 text-xs font-medium text-foreground hidden md:table-cell">
                        {dobStr}
                      </td>

                      {/* Phone */}
                      <td className="py-2.5 px-4 text-xs font-medium text-muted-foreground hidden lg:table-cell">
                        {p.phone ? (
                          <span className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-primary" />{p.phone}</span>
                        ) : '—'}
                      </td>

                      {/* Email */}
                      <td className="py-2.5 px-4 text-xs font-medium text-muted-foreground hidden xl:table-cell">
                        {p.email ? (
                          <span className="flex items-center gap-1.5 truncate max-w-[180px]"><Mail className="w-3 h-3 text-primary" />{p.email}</span>
                        ) : '—'}
                      </td>

                      {/* In Patient Care Team */}
                      <td className="py-2.5 px-4">
                        {p.addedInCareTeam ? (
                          <Badge className="text-[11px] border-0 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 font-semibold px-2 py-0.5 rounded-full">
                            {p.addedInCareTeamDate ? `Yes - ${new Date(p.addedInCareTeamDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : "Yes"}
                          </Badge>
                        ) : (
                          <Badge className="text-[11px] border-0 bg-muted text-muted-foreground font-medium px-2 py-0.5 rounded-full">
                            No
                          </Badge>
                        )}
                      </td>

                      {/* GFE Button & Actions */}
                      <td className="py-2.5 px-4 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(targetMemberId)}&memberName=${encodeURIComponent(p.name)}`}>
                            <Button size="sm" className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1 text-xs rounded-xl shadow-xs">
                              <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                            </Button>
                          </Link>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="w-7 h-7 rounded-xl">
                                <MoreVertical className="w-3.5 h-3.5 text-slate-500 hover:text-slate-700" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem className="cursor-pointer gap-2 font-medium text-primary text-xs" onClick={() => handleOpenPatientProfile(p)}>
                                <User className="w-3.5 h-3.5" /> View Patient Profile
                              </DropdownMenuItem>
                              
                              <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(targetMemberId)}&memberName=${encodeURIComponent(p.name)}`}>
                                <DropdownMenuItem className="cursor-pointer gap-2 font-bold text-emerald-600 dark:text-emerald-400 text-xs">
                                  <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                                </DropdownMenuItem>
                              </Link>

                              <Link href={`/provider/appointments?search=${encodeURIComponent(p.name)}`}>
                                <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                                  <Calendar className="w-3.5 h-3.5 text-slate-500" /> View Appointments
                                </DropdownMenuItem>
                              </Link>

                              {p.addedInCareTeam && p.userId && (
                                <DropdownMenuItem
                                  className="cursor-pointer gap-2 text-red-600 focus:text-red-600 text-xs"
                                  onClick={() => {
                                    if (window.confirm("Remove patient from your care team?")) {
                                      removePatient.mutate({ patientId: p.userId }, {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: getListPatientsQueryKey() });
                                          toast({ title: "Removed from care team", description: "Patient has been removed." });
                                        }
                                      });
                                    }
                                  }}
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Remove from care team
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-auto pt-4 sm:pt-6 border-t border-slate-200 dark:border-slate-800 flex justify-center flex-shrink-0">
          <ListPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
        </div>
      </div>

      {/* Patient Profile & History Modal */}
      <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
        <DialogContent className="w-[95vw] max-w-[760px] max-h-[92vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-6 bg-card border border-border shadow-2xl space-y-4 sm:space-y-6">
          {selectedPatient && (
            <div className="space-y-4 sm:space-y-6">
              {/* Profile Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 pb-4 sm:pb-5 border-b border-border">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <Avatar className="w-12 h-12 sm:w-16 sm:h-16 border-2 border-primary/20 shadow-md shrink-0">
                    {selectedPatient.profilePicture && (
                      <AvatarImage src={`${API_BASE.replace(/\/v1|\/api/, '')}${selectedPatient.profilePicture}`} alt={selectedPatient.name} className="object-cover" />
                    )}
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg sm:text-xl font-extrabold">
                      {selectedPatient.name?.split(" ").map((n: string) => n[0]).join("").slice(0, 2) || "P"}
                    </AvatarFallback>
                  </Avatar>

                  <div className="space-y-0.5 sm:space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-xl sm:text-2xl font-bold text-foreground leading-tight truncate">
                        {selectedPatient.name}
                      </h3>
                      {selectedPatient.gender && (
                        <Badge variant="outline" className="text-[11px] sm:text-xs font-semibold border-primary/20 text-primary bg-primary/10">
                          {selectedPatient.gender}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground font-medium">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-primary" />
                        DOB: {selectedPatient.dateOfBirth || selectedPatient.dob || 'Not specified'}
                      </span>
                      <span className="hidden sm:inline">•</span>
                      <span className="font-mono text-primary font-bold bg-primary/10 px-2 py-0.5 rounded-md">
                        ID: DC-{String(selectedPatient.id).slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(selectedPatient.userId || selectedPatient.id)}&memberName=${encodeURIComponent(selectedPatient.name)}`} className="w-full sm:w-auto">
                    <Button size="sm" className="w-full sm:w-auto h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs gap-1.5 shadow-md shadow-emerald-600/20">
                      <Sparkles className="w-4 h-4" /> Generate GFE
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Stat Cards Strip */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-muted/50 border border-border flex flex-col justify-between">
                  <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1 sm:gap-1.5 truncate">
                    <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary shrink-0" /> Total Visits
                  </span>
                  <span className="text-lg sm:text-xl font-extrabold text-foreground mt-0.5 sm:mt-1">{patientAppointments.length}</span>
                </div>

                <div className="p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-muted/50 border border-border flex flex-col justify-between">
                  <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1 sm:gap-1.5 truncate">
                    <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary shrink-0" /> Estimates
                  </span>
                  <span className="text-lg sm:text-xl font-extrabold text-foreground mt-0.5 sm:mt-1">{patientEstimates.length}</span>
                </div>

                <div className="p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-muted/50 border border-border flex flex-col justify-between">
                  <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1 sm:gap-1.5 truncate">
                    <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" /> Total Value ($)
                  </span>
                  <span className="text-lg sm:text-xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 sm:mt-1 truncate">
                    ${patientAppointments.reduce((sum: number, a: any) => sum + (Number(a.amount) || 0), 0).toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Profile Tabs */}
              <Tabs value={profileTab} onValueChange={(val: any) => setProfileTab(val)} className="w-full">
                <TabsList className="grid grid-cols-3 w-full h-11 sm:h-12 bg-muted/70 p-1 sm:p-1.5 rounded-xl sm:rounded-2xl border border-border">
                  <TabsTrigger value="overview" className="text-[10px] sm:text-xs font-bold rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all px-1 sm:px-2">
                    Overview & Contact
                  </TabsTrigger>
                  <TabsTrigger value="visits" className="text-[10px] sm:text-xs font-bold rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all flex items-center justify-center gap-1 px-1 sm:px-2">
                    <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 hidden sm:inline" /> Past Visits ({patientAppointments.length})
                  </TabsTrigger>
                  <TabsTrigger value="estimates" className="text-[10px] sm:text-xs font-bold rounded-lg sm:rounded-xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all flex items-center justify-center gap-1 px-1 sm:px-2">
                    <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 hidden sm:inline" /> Cost Estimates ({patientEstimates.length})
                  </TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview & Contact */}
                <TabsContent value="overview" className="space-y-4 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Contact Details Card */}
                    <div className="bg-card p-5 rounded-2xl border border-border shadow-xs space-y-4">
                      <div className="flex items-center gap-2.5 pb-3 border-b border-border">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                          <Mail className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-foreground">
                            Contact Details
                          </h4>
                          <p className="text-[11px] text-muted-foreground">Direct communication channels</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/40 border border-border">
                          <span className="text-muted-foreground font-medium">Email</span>
                          <span className="font-bold text-foreground">{selectedPatient.email || "N/A"}</span>
                        </div>

                        <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/40 border border-border">
                          <span className="text-muted-foreground font-medium">Phone</span>
                          <span className="font-bold text-foreground">{selectedPatient.phone || "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Demographics Card */}
                    <div className="bg-card p-5 rounded-2xl border border-border shadow-xs space-y-4">
                      <div className="flex items-center gap-2.5 pb-3 border-b border-border">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                          <ShieldCheck className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-foreground">
                            Demographics & Identity
                          </h4>
                          <p className="text-[11px] text-muted-foreground">Patient record specifications</p>
                        </div>
                      </div>

                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/40 border border-border">
                          <span className="text-muted-foreground font-medium">SSN</span>
                          <span className="font-mono font-bold text-foreground">
                            {selectedPatient.ssn ? `***-**-${selectedPatient.ssn.slice(-4)}` : "Not Provided"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 2: Past Visits */}
                <TabsContent value="visits" className="space-y-3 pt-4">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-foreground">
                      Past Visits & Medical Interactions
                    </h4>
                  </div>

                  {patientAppointments.length === 0 ? (
                    <div className="p-10 text-center bg-card rounded-2xl border border-dashed border-border shadow-xs">
                      <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                      <p className="text-sm font-bold text-foreground">No past visits recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                      {patientAppointments.map((apt: any) => (
                        <div key={apt.id} className="p-4 bg-card rounded-2xl border border-border flex justify-between items-center shadow-xs hover:border-primary/40 transition-all">
                          <div className="space-y-1">
                            <h5 className="font-extrabold text-sm text-foreground">
                              {apt.serviceName || apt.bundle?.name || "Medical Visit"}
                            </h5>
                            <span className="text-xs text-muted-foreground">
                              {new Date(apt.createdAt || Date.now()).toLocaleDateString()}
                            </span>
                          </div>

                          <div className="text-right">
                            <span className="font-black text-base text-primary">${Number(apt.amount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Tab 3: Cost Estimates */}
                <TabsContent value="estimates" className="space-y-3 pt-4">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-foreground">
                      Good Faith Cost Estimates
                    </h4>
                    <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(selectedPatient.userId || selectedPatient.id)}&memberName=${encodeURIComponent(selectedPatient.name)}`}>
                      <Button size="sm" className="h-8 text-xs rounded-xl gap-1.5 font-bold shadow-xs bg-emerald-600 text-white hover:bg-emerald-700">
                        <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                      </Button>
                    </Link>
                  </div>

                  {patientEstimates.length === 0 ? (
                    <div className="p-10 text-center bg-card rounded-2xl border border-dashed border-border shadow-xs">
                      <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                      <p className="text-sm font-bold text-foreground">No cost estimates generated</p>
                      <Link href={`/provider/cost-estimator?memberId=${encodeURIComponent(selectedPatient.userId || selectedPatient.id)}`}>
                        <Button size="sm" className="mt-3 rounded-xl gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 font-bold text-xs">
                          <Sparkles className="w-3.5 h-3.5" /> Generate First GFE
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                      {patientEstimates.map((est: any) => (
                        <div key={est.id} className="p-4 bg-card rounded-2xl border border-border flex justify-between items-center shadow-xs hover:border-primary/40 transition-all">
                          <div className="space-y-1">
                            <h5 className="font-extrabold text-sm text-foreground">
                              {est.name || est.title || "Good Faith Estimate"}
                            </h5>
                            <span className="text-xs text-muted-foreground">{new Date(est.createdAt).toLocaleDateString()}</span>
                          </div>

                          <div className="text-right">
                            <span className="font-black text-base text-emerald-600 dark:text-emerald-400">${Number(est.finalAmount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
